<template>
  <nav v-if="pages > 1" :class="classes" aria-label="pagination">
    <div class="pagination__control">
      <EPaginationControl
        v-if="!isFirst"
        side="left"
        :href="previousUrl"
        :aria-label="$t('common.pagination.previous')"
        data-test-id="control-left"
      />
    </div>
    <div class="pagination__pages">
      <template v-for="page in visiblePages">
        <ELink
          v-if="page !== current"
          :key="page"
          :href="getPageUrl(page)"
          color="bare"
          class="pagination__page"
          :title="getPageTitle(page)"
          data-test-id="page"
        >
          <!-- TODO: merge with below -->
          <span class="pagination__page-text">
            {{ $t('common.pagination.page') }}
          </span>
          {{ formatPage(page) }}
        </ELink>
        <div
          v-else
          :key="page"
          class="pagination__page pagination__page--current"
          :title="getPageTitle(page)"
          data-test-id="current"
          aria-current="page"
        >
          <span class="pagination__current-text">
            {{ $t('common.pagination.page') }}
          </span>
          {{ formatPage(page) }}
        </div>
      </template>
    </div>
    <div class="pagination__control">
      <EPaginationControl
        v-if="!isLast"
        side="right"
        :href="nextUrl"
        :aria-label="$t('common.pagination.next')"
        data-test-id="control-right"
      />
    </div>
  </nav>
</template>

<script>
// TODO: switch to ~~ aliases when flora supports it
import { isNaturalNumber, isPositiveNumber } from '~~/common/js/universal/validators';
import classes from '~~/common/vue/mixins/classes';
import ELink from '~~/common/vue/components/link';
import EPaginationControl from './control';

export const MAX_VISIBLE_PAGES = 9;
export const invalidCurrentError = 'Current page cannot be higher or equal than pages.';

export default {
  name: 'EPagination',
  components: {
    ELink,
    EPaginationControl,
  },
  mixins: [classes],
  props: {
    /**
     * 0-based index
     */
    current: {
      type: Number,
      required: true,
      validator: isNaturalNumber,
    },
    pages: {
      type: Number,
      required: true,
      validator: isPositiveNumber,
    },
  },
  computed: {
    isFirst() {
      return this.current === 0;
    },
    isLast() {
      return this.current === this.pages - 1;
    },
    formattedCurrent() {
      return this.current + 1;
    },
    previousUrl() {
      return this.getPageUrl(this.current - 1);
    },
    visiblePages() {
      const max = Math.floor(MAX_VISIBLE_PAGES / 2);
      const start = Math.max(0, this.current - max);
      const end = Math.min(this.pages, this.current + max + 1);
      const length = end - start;
      return Array.from({ length }, (value, index) => index + start);
    },
    nextUrl() {
      return this.getPageUrl(this.current + 1);
    },
  },
  created() {
    const valid = ['current', 'pages'].every((prop) => this.$options.props[prop].validator(this[prop]));
    if (valid && this.current > this.pages - 1) {
      // eslint-disable-next-line no-console
      console.error(invalidCurrentError);
    }
  },
  methods: {
    getPageUrl(page) {
      const query = { ...this.$route.query, p: page };
      const { href } = this.$router.resolve({ query });
      return href;
    },
    getPageTitle(page) {
      const formatted = this.formatPage(page);
      return `${this.$t('common.pagination.page')} ${formatted}`;
    },
    formatPage(page) {
      return page + 1;
    },
  },
};
</script>

<style lang="scss" scoped>
$control-size: $space-3l;

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
  font-size: $font-m;

  @include desktop {
    justify-content: center;
    max-width: none;
  }
}

.pagination__control {
  width: $control-size;
  height: $control-size;
}

.pagination__pages {
  @extend %ellipsis;

  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: $space-1s;
  padding-right: $space-1s;
  text-align: center;
}

.pagination__page {
  padding: ($space-s + 1) $space-s $space-s;
  line-height: $line-height-m;

  &:not(.pagination__page--current) {
    display: none;

    @include desktop {
      display: inline-flex;
    }
  }
}

.pagination__page--current {
  @extend %ellipsis;

  min-width: 0;

  @include desktop {
    overflow: visible;
    min-width: min-content;
    font-weight: $font-weight-1l;
  }
}

.pagination__page-text {
  @extend %sr-only;
}

.pagination__current-text {
  @include desktop {
    display: none;
  }
}
</style>

<docs>
First page

```js
<EPagination
  :current="0"
  :pages="4"
  base-url="http://localhost/search?q=ecosia"
/>
```

Middle page

```js
<EPagination
  :current="5"
  :pages="9"
  base-url="http://localhost/search?q=ecosia"
/>
```

Last page

```js
<EPagination
  :current="8"
  :pages="9"
  base-url="http://localhost/search?q=ecosia"
/>
```

First page + pages count higher than max (9):

```js
<EPagination
  :current="0"
  :pages="12"
  base-url="http://localhost/search?q=ecosia"
/>
```
</docs>
